import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/components/header.css";
import Logo from "../assets/icons/logo.svg";
import ChevronDown from "../assets/icons/chevron-down.svg";
import ChevronUp from "../assets/icons/chevron-up.svg";
import MenuIcon from "../assets/icons/menu-icon.svg";
import CloseIcon from "../assets/icons/close-icon.svg";

const dropdownVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [desktopActiveDropdown, setDesktopActiveDropdown] = useState(null);
  const [mobileOpenDropdowns, setMobileOpenDropdowns] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1170);

  const headerRef = useRef(null);
  const navigate = useNavigate();

  const closeAll = () => {
    setIsMenuOpen(false);
    setDesktopActiveDropdown(null);
    setMobileOpenDropdowns([]);
  };

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isMenuOpen) {
      setDesktopActiveDropdown(null);
      setMobileOpenDropdowns([]);
    }
  }, [isMenuOpen]);

  const handleDropdownToggle = (menuKey) => {
    if (isMobile) {
      if (mobileOpenDropdowns.includes(menuKey)) {
        setMobileOpenDropdowns((prev) => prev.filter((k) => k !== menuKey));
      } else {
        setMobileOpenDropdowns((prev) => [...prev, menuKey]);
      }
    } else {
      setDesktopActiveDropdown((prev) => (prev === menuKey ? null : menuKey));
    }
  };

  const isDropdownOpen = (menuKey) =>
    isMobile
      ? mobileOpenDropdowns.includes(menuKey)
      : desktopActiveDropdown === menuKey;

  const handleLinkClick = () => {
    closeAll();
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1170);
      if (window.innerWidth > 1170) {
        closeAll();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!headerRef.current) return;
      if (!headerRef.current.contains(e.target)) {
        closeAll();
      } else {
        const isButtonOrDropdown = e.target.closest(
          ".nav-btn, .dropdown-menu, .chevron-btn"
        );
        if (!isButtonOrDropdown) {
          setDesktopActiveDropdown(null);
        }
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  return (
    <>
      <div
        className={`overlay ${isMenuOpen ? "visible" : ""}`}
        onClick={closeAll}
      ></div>

      <motion.header
        className="header"
        ref={headerRef}
        layout
        transition={{ layout: { duration: 0.25 } }}
      >
        <motion.div className="header-container" layout>
          <div className="header-logo">
            <Link to="/">
              <img src={Logo} alt="Bishkek Petroleum" />
            </Link>
          </div>

          <button className="menu-toggle-button" onClick={toggleMenu}>
            <img src={isMenuOpen ? CloseIcon : MenuIcon} alt="Menu Toggle" />
          </button>

          {isMobile ? (
            <AnimatePresence>
              {isMenuOpen && (
                <motion.nav
                  className="header-nav"
                  initial={{ opacity: 0, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="nav-item">
                    <Link to="/" className="nav-btn" onClick={handleLinkClick}>
                      Главная
                    </Link>
                  </div>

                  <div className="nav-item dropdown-parent">
                    <Link
                      to="/about_company"
                      className="nav-btn"
                      onClick={handleLinkClick}
                    >
                      О компании
                    </Link>
                    <button
                      type="button"
                      className="chevron-btn"
                      onClick={() => handleDropdownToggle("about")}
                    >
                      <img
                        src={isDropdownOpen("about") ? ChevronUp : ChevronDown}
                        alt="chevron"
                        className="chevron-icon"
                      />
                    </button>
                    <AnimatePresence>
                      {isDropdownOpen("about") && (
                        <motion.ul
                          className="dropdown-menu"
                          variants={dropdownVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          transition={{ duration: 0.25 }}
                        >
                          <li
                            className="dropdown-link"
                            onClick={() => {
                              navigate("/about_company/news");
                              handleLinkClick();
                            }}
                          >
                            Новости
                          </li>
                          <li
                            className="dropdown-link"
                            onClick={() => {
                              navigate("/about_company/vacancies");
                              handleLinkClick();
                            }}
                          >
                            Вакансии
                          </li>
                          <li
                            className="dropdown-link"
                            onClick={() => {
                              navigate("/about_company/social_activities");
                              handleLinkClick();
                            }}
                          >
                            Соц. деятельность
                          </li>
                          <li
                            className="dropdown-link"
                            onClick={() => {
                              navigate("/about_company/contacts");
                              handleLinkClick();
                            }}
                          >
                            Контакты
                          </li>
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="nav-item dropdown-parent">
                    <Link
                      to="/stations"
                      className="nav-btn"
                      onClick={handleLinkClick}
                    >
                      Карта АЗС
                    </Link>
                    <button
                      type="button"
                      className="chevron-btn"
                      onClick={() => handleDropdownToggle("stations")}
                    >
                      <img
                        src={isDropdownOpen("stations") ? ChevronUp : ChevronDown}
                        alt="chevron"
                        className="chevron-icon"
                      />
                    </button>
                    <AnimatePresence>
                      {isDropdownOpen("stations") && (
                        <motion.ul
                          className="dropdown-menu"
                          variants={dropdownVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          transition={{ duration: 0.25 }}
                        >
                          <li
                            className="dropdown-link"
                            onClick={() => {
                              navigate("/stations/mini_markets");
                              handleLinkClick();
                            }}
                          >
                            Мини маркеты
                          </li>
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="nav-item dropdown-parent">
                    <Link
                      to="/for_clients"
                      className="nav-btn"
                      onClick={handleLinkClick}
                    >
                      Оптовым клиентам
                    </Link>
                    <button
                      type="button"
                      className="chevron-btn"
                      onClick={() => handleDropdownToggle("clients")}
                    >
                      <img
                        src={isDropdownOpen("clients") ? ChevronUp : ChevronDown}
                        alt="chevron"
                        className="chevron-icon"
                      />
                    </button>
                    <AnimatePresence>
                      {isDropdownOpen("clients") && (
                        <motion.ul
                          className="dropdown-menu"
                          variants={dropdownVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          transition={{ duration: 0.25 }}
                        >
                          <li
                            className="dropdown-link"
                            onClick={() => {
                              navigate("/government_procurements");
                              handleLinkClick();
                            }}
                          >
                            Закупки
                          </li>
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="nav-item">
                    <Link to="/for_clients/usefull_info" className="nav-btn" onClick={handleLinkClick}>
                      Полезная информация
                    </Link>
                  </div>
                </motion.nav>
              )}
            </AnimatePresence>
          ) : (
            <nav className={`header-nav ${isMenuOpen ? "open" : ""}`}>
              <div className="nav-item">
                <Link to="/" className="nav-btn" onClick={handleLinkClick}>
                  Главная
                </Link>
              </div>

              <div className="nav-item dropdown-parent">
                <Link
                  to="/about_company"
                  className="nav-btn"
                  onClick={handleLinkClick}
                >
                  О компании
                </Link>
                <button
                  type="button"
                  className="chevron-btn"
                  onClick={() => handleDropdownToggle("about")}
                >
                  <img
                    src={isDropdownOpen("about") ? ChevronUp : ChevronDown}
                    alt="chevron"
                    className="chevron-icon"
                  />
                </button>
                <AnimatePresence>
                  {isDropdownOpen("about") && (
                    <motion.ul
                      className="dropdown-menu"
                      variants={dropdownVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      transition={{ duration: 0.25 }}
                    >
                      <li
                        className="dropdown-link"
                        onClick={() => {
                          navigate("/about_company/news");
                          handleLinkClick();
                        }}
                      >
                        Новости
                      </li>
                      <li
                        className="dropdown-link"
                        onClick={() => {
                          navigate("/about_company/vacancies");
                          handleLinkClick();
                        }}
                      >
                        Вакансии
                      </li>
                      <li
                        className="dropdown-link"
                        onClick={() => {
                          navigate("/about_company/social_activities");
                          handleLinkClick();
                        }}
                      >
                        Соц. деятельность
                      </li>
                      <li
                        className="dropdown-link"
                        onClick={() => {
                          navigate("/about_company/contacts");
                          handleLinkClick();
                        }}
                      >
                        Контакты
                      </li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>

              <div className="nav-item dropdown-parent">
                <Link
                  to="/stations"
                  className="nav-btn"
                  onClick={handleLinkClick}
                >
                  Карта АЗС
                </Link>
                <button
                  type="button"
                  className="chevron-btn"
                  onClick={() => handleDropdownToggle("stations")}
                >
                  <img
                    src={isDropdownOpen("stations") ? ChevronUp : ChevronDown}
                    alt="chevron"
                    className="chevron-icon"
                  />
                </button>
                <AnimatePresence>
                  {isDropdownOpen("stations") && (
                    <motion.ul
                      className="dropdown-menu"
                      variants={dropdownVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      transition={{ duration: 0.25 }}
                    >
                      <li
                        className="dropdown-link"
                        onClick={() => {
                          navigate("/stations/mini_markets");
                          handleLinkClick();
                        }}
                      >
                        Мини маркеты
                      </li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>

              <div className="nav-item dropdown-parent">
                <Link
                  to="/for_clients"
                  className="nav-btn"
                  onClick={handleLinkClick}
                >
                  Оптовым клиентам
                </Link>
                <button
                  type="button"
                  className="chevron-btn"
                  onClick={() => handleDropdownToggle("clients")}
                >
                  <img
                    src={isDropdownOpen("clients") ? ChevronUp : ChevronDown}
                    alt="chevron"
                    className="chevron-icon"
                  />
                </button>
                <AnimatePresence>
                  {isDropdownOpen("clients") && (
                    <motion.ul
                      className="dropdown-menu"
                      variants={dropdownVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      transition={{ duration: 0.25 }}
                    >
                      <li
                        className="dropdown-link"
                        onClick={() => {
                          navigate("/government_procurements");
                          handleLinkClick();
                        }}
                      >
                        Закупки
                      </li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>

              <div className="nav-item">
                <Link to="/for_clients/usefull_info" className="nav-btn" onClick={handleLinkClick}>
                  Полезная информация
                </Link>
              </div>
            </nav>
          )}

          {!isMenuOpen && !isMobile && (
            <div className="header-contact">
              <span className="header-contact-number">996557294949</span>
              <span className="header-contact-text">горячая линия</span>
            </div>
          )}
        </motion.div>
      </motion.header>
    </>
  );
};

export default Header;
